/**
 * The M6 station panels (plan P27) as M3 `StationServicePanel`s, so they mount in M2's station screen through the
 * M3 bridge (`bridge/m2/stationTabs.ts`) like the Station, Wire, Charter and Registry tabs:
 *
 * - **Shipyard** (`shipyard`, at stations whose strategic view has a shipyard): buy a hull through the `fleet`
 *   service (`buyShip`: the wallet pays while a sole trader, the company account after founding).
 * - **Hangar** (`hangar`, every station): the owned ships with switch, ferry, transfer and insure through an injected
 *   `HangarActions` (`createHangarActions`: insurance is the `fleet` service's; switch, ferry and transfer are P24's
 *   to wire).
 * - **Roster** (`roster`, every station): hire from the station's captain roster (`hireCaptain`), then, when a ship
 *   is chosen, assign the new captain to it (`assignCaptain`, one captain per ship).
 *
 * Each panel reads its views when it mounts and after every action; the models (`models/*.ts`) hold every rule.
 * UI events only.
 */
import type { ReactNode } from 'react'
import type { Career } from '../../career/types'
import { SIM_FACTION_IDS, type M2Data, type ModuleId, type SlotId } from '../../data/m2Schemas'
import { logger } from '../../core/logger'
import type { FleetService } from '../../fleet-link/service'
import { clearPinOnPurchase, pinHull } from '../../hud/goal'
import { hulksConfig } from '../../hulks/data'
import type { ScrapForm } from '../../hulks/scrap'
import type { PanelContext, StationServicePanel } from '../../reach/ports'
import type { StrategicClient } from '../../strategic/client'
import type { CorpFactionId, FleetView } from '../../strategic/types'
import { mountReact } from '../strategic/mount'
import { factionName } from '../strategic/names'
import { Compare } from './Compare'
import { Hangar } from './Hangar'
import { panelCatalog } from './models/catalog'
import { compareDataFrom, compareModel, toggleCompare, type CompareData } from './models/compare'
import { hangarModel, type HangarActionKind, type HangarInput } from './models/hangar'
import { caught, outcomeText, type ActionOutcome } from './models/outcome'
import { canAssignTo, cycleAssign, hiredText, rosterModel } from './models/roster'
import { gatePlayerOf, shipyardRules } from './models/gates'
import { boughtText, companyRelationOf, shipyardModel, type ShipyardInput } from './models/shipyard'
import { Roster } from './Roster'
import { Shipyard } from './Shipyard'

/**
 * What the Hangar's buttons do. `setInsured` is the P21 `fleet` service's `setInsurance`; `switchTo`, `ferry` and
 * `transfer` belong to P24's switch module (`src/fleet-link/switch.ts`) and are wired by passing them to
 * `createHangarActions`.
 */
export interface HangarActions {
  /** Boards owned ship `shipId`, docked at this station (the flown ship is stowed). */
  switchTo(shipId: string): Promise<ActionOutcome>
  /** Orders `shipId` to fly to `stationId` (a real trip). */
  ferry(shipId: string, stationId: string): Promise<ActionOutcome>
  /** Books the paid transfer service to bring `shipId` to `stationId`. */
  transfer(shipId: string, stationId: string): Promise<ActionOutcome>
  /** Turns one ship's insurance on or off (on needs the lease owned). */
  setInsured(shipId: string, on: boolean): Promise<ActionOutcome>
  /** M9 (CH-12b): fixes flaw `flaw` of `shipId` at yard station `stationId` (the fleet service's `fixHullFlaw`). */
  fixFlaw(shipId: string, flaw: number, stationId: string, yard: boolean): Promise<ActionOutcome>
  /** M9 (CH-12b): scraps claimed ship `shipId` for credits or its parts (the fleet service's `scrapClaimedShip`). */
  scrap(shipId: string, form: ScrapForm): Promise<ActionOutcome>
  /** PF-2: abandons the open hulk claim `key` from the hangar (the fleet service's `closeHulkClaim`, the card's path). */
  abandonClaim(key: string): Promise<ActionOutcome>
  /** M10 P5a3c: renames owned ship `shipId` (the fleet service's `renameShip`). Absent: the hangar has no Rename. */
  rename?(shipId: string, name: string): Promise<ActionOutcome>
}

/** The `fleet` service calls the panels make. */
export type PanelFleet = Pick<FleetService, 'fleetView' | 'run' | 'buyShip' | 'setInsurance' | 'ownershipUnlocked' | 'fixHullFlaw' | 'scrapClaimedShip' | 'closeHulkClaim'>

/**
 * What the panels read from the strategic client (`StrategicClient` satisfies it). `relations` (M10 P4a W5a) gives
 * the shop the founded company's relation with the yard's owner (`companyRelationOf`).
 */
export type PanelFleetClient = Pick<StrategicClient, 'systemView' | 'stationView' | 'captainView' | 'corpView' | 'relations'>

export interface FleetPanelDeps {
  /** The `fleet` service, or null while the link is not up. */
  fleet(): PanelFleet | null
  client: PanelFleetClient
  /** The running Career, or null. */
  career(): Career | null
  /** M2's power rule for the stock fit a new ship gets (`powerDraw` / `powerBudget`). */
  stockFit(): { draw: number; budget: number }
  /**
   * M10 P5a3a: the compare panel's module data and the start fit a new ship is delivered with (`m2.data`);
   * absent: the shop offers no Compare button.
   */
  compare?: { modules: Pick<M2Data, 'modules'>; startFit: Readonly<Record<SlotId, ModuleId>> }
  /** M10 P5a2a/P5a3a: the Career writer (the runtime's `applyDevCommand`) for the shop's Pin; absent: no Pin button. */
  applyCareer?(name: string, cmd: (career: Career) => Career): unknown
  hangar: HangarActions
  /** Where a ship is, in words (defaults to the id). */
  placeName?: HangarInput['placeName']
}

const NO_FLEET: ActionOutcome = { ok: false, error: 'noFleet' }

/** P24's switch, ferry and transfer (the lander passes `src/fleet-link/switch.ts`'s exports). */
export type HangarMoves = Pick<HangarActions, 'switchTo' | 'ferry' | 'transfer'>

/**
 * The Hangar's actions over the `fleet` service. `setInsured` is the service's `setInsurance`. Until P24's moves
 * are passed in, `ferry` and `transfer` send the worker's `ferryShip` / `transferService` commands as they are and
 * `switchTo` is refused (`notWired`).
 */
export function createHangarActions(
  fleet: () => (Pick<PanelFleet, 'run' | 'setInsurance' | 'fixHullFlaw' | 'scrapClaimedShip' | 'closeHulkClaim'> & Partial<Pick<FleetService, 'renameShip'>>) | null,
  moves: Partial<HangarMoves> = {},
): HangarActions {
  return {
    rename: (shipId, name): Promise<ActionOutcome> => fleet()?.renameShip?.(shipId, name) ?? Promise.resolve(NO_FLEET),
    switchTo: moves.switchTo ?? ((): Promise<ActionOutcome> => Promise.resolve({ ok: false, error: 'notWired' })),
    ferry: moves.ferry ?? ((shipId, stationId): Promise<ActionOutcome> => fleet()?.run({ type: 'ferryShip', shipId, stationId }) ?? Promise.resolve(NO_FLEET)),
    transfer: moves.transfer ?? ((shipId, stationId): Promise<ActionOutcome> => fleet()?.run({ type: 'transferService', shipId, stationId }) ?? Promise.resolve(NO_FLEET)),
    setInsured: (shipId, on): Promise<ActionOutcome> => fleet()?.setInsurance(shipId, on) ?? Promise.resolve(NO_FLEET),
    fixFlaw: (shipId, flaw, stationId, yard): Promise<ActionOutcome> => fleet()?.fixHullFlaw(shipId, flaw, stationId, yard) ?? Promise.resolve(NO_FLEET),
    scrap: (shipId, form): Promise<ActionOutcome> => fleet()?.scrapClaimedShip(shipId, form) ?? Promise.resolve(NO_FLEET),
    abandonClaim: (key): Promise<ActionOutcome> => fleet()?.closeHulkClaim(key) ?? Promise.resolve(NO_FLEET),
  }
}

// ---------------------------------------------------------------------------------------------
// A panel that reads its state, renders it, and re-reads after each action
// ---------------------------------------------------------------------------------------------

interface PanelUi {
  status: string | null
  busy: boolean
  /** Runs an action: busy while it runs, then its status line and a re-read. */
  act(fn: () => Promise<string>): void
  /** Re-renders with the current state (a local choice changed). */
  redraw(): void
}

interface AsyncPanelSpec<S> {
  id: string
  title: string
  order: number
  available: StationServicePanel['available']
  load(stationId: string): Promise<S>
  view(state: S | null, ui: PanelUi, stationId: string): ReactNode
}

function asyncPanel<S>(spec: AsyncPanelSpec<S>): StationServicePanel {
  return {
    id: spec.id,
    title: spec.title,
    order: spec.order,
    available: spec.available,
    mount(el: HTMLElement, ctx: PanelContext): () => void {
      const stationId = ctx.station.id
      const mount = mountReact(el, 'sui-mount')
      let state: S | null = null
      let alive = true
      const ui: PanelUi = { status: null, busy: false, act, redraw: render }
      function render(): void {
        if (!alive) return
        mount.render(spec.view(state, ui, stationId))
        ctx.refresh()
      }
      async function reload(): Promise<void> {
        try {
          state = await spec.load(stationId)
        } catch (err: unknown) {
          logger.warn(`fleet panel ${spec.id}: could not read its views`, err)
        }
        render()
      }
      function act(fn: () => Promise<string>): void {
        if (ui.busy) return
        ui.busy = true
        render()
        void fn()
          .catch((err: unknown) => outcomeText(caught(err), ''))
          .then(async (text) => {
            ui.status = text
            await reload()
          })
          .finally(() => {
            ui.busy = false
            render()
          })
      }
      render()
      void reload()
      return (): void => {
        alive = false
        mount.unmount()
      }
    },
  }
}

function Loading(p: { testId: string }): ReactNode {
  return (
    <div className="sui fp-panel" data-testid={p.testId}>
      <p className="sui-empty">Reading the fleet records…</p>
    </div>
  )
}

function wallet(career: Career | null): number {
  return career?.wallet ?? 0
}

async function fleetOf(deps: FleetPanelDeps): Promise<{ fleet: PanelFleet; view: FleetView }> {
  const fleet = deps.fleet()
  if (fleet === null) throw new Error('the fleet link is not up')
  return { fleet, view: await fleet.fleetView() }
}

// ---------------------------------------------------------------------------------------------
// The three panels
// ---------------------------------------------------------------------------------------------

/**
 * The Career's standing with strategic faction `owner` (0 for a faction the Career keeps none with). The M2 and
 * strategic faction ids match (`charter/careerBridge.ts strategicFactionOf`); that module stays lazily loaded.
 */
export function standingWith(career: Pick<Career, 'standings'>, owner: CorpFactionId): number {
  for (const f of SIM_FACTION_IDS) if (f === owner) return career.standings[f]
  return 0
}

/** True when the strategic view of `stationId` (this system's) has a shipyard. */
export function hasShipyard(client: Pick<PanelFleetClient, 'systemView'>, stationId: string): boolean {
  const v = client.systemView
  if (v === null) return false
  for (const s of v.stations) if (s.id === stationId) return s.shipyard !== null
  return false
}

/**
 * The Shipyard's input at `stationId`: the yard, the personal standing with its owner, the payer's funds, and (M10
 * P4a W5a, D-866 spill 1) the founded company's relation with the owner from the latest relations view, so the shop's
 * embargo price and refusal follow the company's relation as the worker's `buyShipAt` does.
 */
export async function loadShipyardInput(deps: FleetPanelDeps, stationId: string): Promise<{ input: ShipyardInput; fleet: PanelFleet }> {
  const [{ fleet, view }, station] = await Promise.all([fleetOf(deps), deps.client.stationView(stationId)])
  const career = deps.career()
  const standing = career === null ? 0 : standingWith(career, station.owner)
  const funds = view.soleTrader ? wallet(career) : (await deps.client.corpView()).treasury
  const input: ShipyardInput = {
    yard: station.shipyard === null ? null : { id: station.id, name: station.name, factionName: factionName(station.owner), wares: station.wares },
    standing,
    soleTrader: view.soleTrader,
    funds,
    fleetSize: view.ships.length,
    cap: view.cap,
    stockFit: deps.stockFit(),
    companyRelation: companyRelationOf(deps.client.relations, station.owner, view.soleTrader),
    // M10 P5a1b1: the hull gates' player and yard owner, only once shipyard.json `enabled` is on (off: today's input).
    ...(career === null || !shipyardRules().enabled ? {} : { gate: { player: gatePlayerOf(career, !view.soleTrader), owner: station.owner } }),
    // M10 P5a2a: the pinned goal hull (the model reads it only while the gates are on).
    ...(career?.pinnedHull === undefined ? {} : { pinnedHull: career.pinnedHull.hull }),
    // M10 P5a3c: the next ship's default name, for the name field's placeholder.
    ...(view.nextName === undefined ? {} : { nextName: view.nextName }),
  }
  return { input, fleet }
}

export function createShipyardPanel(deps: FleetPanelDeps): StationServicePanel {
  const cfg = panelCatalog().config
  /** M10 P5a3a: the hulls picked to compare (kept between mounts, like the roster's `assignTo`; empty: no panel). */
  let picks: string[] = []
  let compareData: CompareData | null = null
  const cmp = deps.compare
  const apply = deps.applyCareer
  return asyncPanel<{ input: ShipyardInput; fleet: PanelFleet }>({
    id: 'shipyard',
    title: 'Shipyard',
    order: cfg.order.shipyard,
    available: (site) => deps.fleet() !== null && hasShipyard(deps.client, site.id),
    load: (stationId) => loadShipyardInput(deps, stationId),
    view(state, ui, stationId) {
      if (state === null) return <Loading testId="fleet-shipyard" />
      const model = shipyardModel(state.input)
      const shop = (
        <Shipyard
          {...(state.input.nextName === undefined ? {} : { defaultName: state.input.nextName })}
          {...(apply === undefined
            ? {}
            : {
                // The pin is a Career write; the act re-reads the input so the row reads "Pinned".
                onPin: (hull: string) =>
                  ui.act(() => {
                    apply('pin hull', (c) => pinHull(c, hull, stationId))
                    return Promise.resolve(pinnedText(model.rows.find((r) => r.hull === hull)?.name ?? hull))
                  }),
              })}
          {...(cmp === undefined
            ? {}
            : {
                onCompare: (hull: string) => {
                  picks = toggleCompare(picks, hull)
                  ui.redraw()
                },
              })}
          model={model}
          status={ui.status}
          busy={ui.busy}
          onBuy={(hull, name) =>
            ui.act(async () => {
              const row = model.rows.find((r) => r.hull === hull)
              // The yard owner's standing, as today; the service sends the maker's only once the gates are on (review F2).
              // M10 P5a3b: a typed name rides along; none = today's call.
              const r = await (name === undefined
                ? state.fleet.buyShip(stationId, hull, state.input.standing, state.input.gate?.owner)
                : state.fleet.buyShip(stationId, hull, state.input.standing, state.input.gate?.owner, name))
              // M10 P5a2a: buying the pinned hull clears the goal (a Career without that pin is unchanged).
              if (r.ok && apply !== undefined) apply('clear pin on purchase', (c) => clearPinOnPurchase(c, hull))
              return outcomeText(r, boughtText(row?.name ?? hull))
            })
          }
        />
      )
      if (cmp === undefined || picks.length === 0) return shop
      compareData ??= compareDataFrom(cmp.modules)
      const career = deps.career()
      const compare = compareModel(
        {
          flown: career === null ? null : { defId: career.ship.defId, fitted: career.ship.fitted },
          startFit: cmp.startFit,
          rows: model.rows,
          picks,
          gatesOn: shipyardRules().enabled,
        },
        panelCatalog(),
        compareData,
      )
      return (
        <>
          {shop}
          <Compare
            model={compare}
            onRemove={(hull) => {
              picks = toggleCompare(picks, hull)
              ui.redraw()
            }}
            onClose={() => {
              picks = []
              ui.redraw()
            }}
          />
        </>
      )
    },
  })
}

/** M10 P5a2a: the status line after a Pin. */
export function pinnedText(name: string): string {
  return `${name} pinned as your goal: its steps show on the HUD.`
}

export function createHangarPanel(deps: FleetPanelDeps): StationServicePanel {
  const cfg = panelCatalog().config
  return asyncPanel<HangarInput>({
    id: 'hangar',
    title: 'Hangar',
    order: cfg.order.hangar,
    available: () => deps.fleet() !== null,
    async load(stationId) {
      const { fleet, view } = await fleetOf(deps)
      const career = deps.career()
      const input: HangarInput = {
        stationId,
        ships: view.ships,
        flownShip: career?.flownShip ?? null,
        ownershipUnlocked: fleet.ownershipUnlocked(),
        soleTrader: view.soleTrader,
        wallet: wallet(career),
        yard: hasShipyard(deps.client, stationId),
        career,
      }
      if (deps.placeName !== undefined) input.placeName = deps.placeName
      return input
    },
    view(state, ui, stationId) {
      if (state === null) return <Loading testId="fleet-hangar" />
      const model = hangarModel(state)
      const onAction = (shipId: string, action: HangarActionKind, flaw?: number): void => {
        const row = [...model.here, ...model.away].find((r) => r.id === shipId)
        if (row === undefined) return
        ui.act(async () => {
          switch (action) {
            case 'switch':
              return outcomeText(await deps.hangar.switchTo(shipId), `Now flying the ${row.name}.`)
            case 'ferry':
              return outcomeText(await deps.hangar.ferry(shipId, stationId), `The ${row.name} is on its way here.`)
            case 'transfer':
              return outcomeText(await deps.hangar.transfer(shipId, stationId), `The ${row.name} is booked with the transfer service.`)
            case 'insure': {
              const on = !row.insured
              return outcomeText(await deps.hangar.setInsured(shipId, on), on ? `The ${row.name} is insured.` : `The ${row.name} is no longer insured.`)
            }
            case 'fixFlaw': {
              const f = row.flaws.find((x) => x.index === flaw)
              if (f === undefined) return outcomeText({ ok: false, error: 'noFlaw' }, '')
              return outcomeText(await deps.hangar.fixFlaw(shipId, f.index, stationId, state.yard === true), `The yard fixed the ${row.name}: ${f.text}.`)
            }
            case 'scrapCredits':
              return outcomeText(await deps.hangar.scrap(shipId, 'credits'), `The ${row.name} is scrapped.`)
            case 'scrapGoods':
              return outcomeText(await deps.hangar.scrap(shipId, 'goods'), `The ${row.name} is scrapped; the parts are in your hold.`)
            case 'abandonClaim':
              return '' // a claim row's button (`onAbandon`), never a ship row's
          }
        })
      }
      const onAbandon = (key: string): void => {
        ui.act(async () => outcomeText(await deps.hangar.abandonClaim(key), hulksConfig().text.live.kitAbandoned))
      }
      // M10 P5a3c: the hangar's Rename (the worker's `badName`/`nameTaken` refusals show in the status line).
      const rename = deps.hangar.rename
      const onRename =
        rename === undefined
          ? undefined
          : (shipId: string, name: string): void => {
              ui.act(async () => outcomeText(await rename(shipId, name), `The ship is now the ${name}.`))
            }
      return <Hangar model={model} status={ui.status} busy={ui.busy} onAction={onAction} onAbandon={onAbandon} onRename={onRename} />
    },
  })
}

export function createRosterPanel(deps: FleetPanelDeps): StationServicePanel {
  /** The ship the next hire goes to (kept between mounts; the model drops a choice that is no longer valid). */
  let assignTo: string | null = null
  return asyncPanel<{ fleet: PanelFleet; view: FleetView; roster: Awaited<ReturnType<PanelFleetClient['captainView']>> }>({
    id: 'roster',
    title: panelCatalog().config.labels.roster.tab,
    order: panelCatalog().config.order.roster,
    available: () => deps.fleet() !== null,
    async load(stationId) {
      const [{ fleet, view }, roster] = await Promise.all([fleetOf(deps), deps.client.captainView(stationId)])
      return { fleet, view, roster }
    },
    view(state, ui, stationId) {
      if (state === null) return <Loading testId="fleet-roster" />
      const model = rosterModel({
        stationId,
        roster: state.roster.roster,
        hired: state.roster.hired,
        hiredCap: state.roster.hiredCap,
        ships: state.view.ships,
        assignTo,
        soleTrader: state.view.soleTrader,
      })
      return (
        <Roster
          title={panelCatalog().config.labels.roster.title}
          model={model}
          status={ui.status}
          busy={ui.busy}
          onAssign={(d) => {
            assignTo = cycleAssign(model.targets, model.assignTo, d)
            ui.redraw()
          }}
          onHire={(candidate) =>
            ui.act(async () => {
              const row = model.rows.find((r) => r.candidate === candidate)
              const name = row?.name ?? 'Captain'
              const hired = await state.fleet.run({ type: 'hireCaptain', stationId, candidate })
              if (!hired.ok) return outcomeText(hired, '')
              const ship = model.assignTo
              if (ship === null || hired.createdId === undefined || !canAssignTo(state.view.ships, ship)) return hiredText(name, null)
              const assigned = await state.fleet.run({ type: 'assignCaptain', captainId: hired.createdId, shipId: ship, stationId: null })
              assignTo = null
              const shipName = state.view.ships.find((s) => s.id === ship)?.name ?? ship
              return assigned.ok ? hiredText(name, shipName) : `${hiredText(name, null)} ${outcomeText(assigned, '')}`
            })
          }
        />
      )
    },
  })
}

/** The three panels, ready to register into `ctx.reach.services`. */
export function createFleetStationPanels(deps: FleetPanelDeps): StationServicePanel[] {
  return [createShipyardPanel(deps), createHangarPanel(deps), createRosterPanel(deps)]
}
